import React, { useState, useEffect, useRef, useMemo } from 'react';
import { GoogleMap, PolygonF, MarkerF, useLoadScript, OverlayView } from '@react-google-maps/api';
import { supabase } from '../../supabaseClient';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/20/solid';

export default function SmartMapModal({ clusters, onClose, onProjectCreated }) {
  const [selectedClusters, setSelectedClusters] = useState([]);
  const [selectedClusterIndex, setSelectedClusterIndex] = useState(0);
  const [projectName, setProjectName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdClusters, setCreatedClusters] = useState(new Set());
  const [mergedClusters, setMergedClusters] = useState([]);
  const mapRef = useRef(null);
  const shouldPanMapRef = useRef(false); // Track when to pan the map
  const [successMessage, setSuccessMessage] = useState('');

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: 'AIzaSyBBfpoVAWYZaujXG5t93mFIJKqk3cwHynw',
  });

  const MAX_MARKERS = 100;

  // Calculate the centroid of a polygon for label positioning
  const calculatePolygonCentroid = (polygon) => {
    let totalLat = 0;
    let totalLng = 0;
    const pointCount = polygon.length;

    polygon.forEach(point => {
      totalLat += point.lat;
      totalLng += point.lng;
    });

    return {
      lat: totalLat / pointCount,
      lng: totalLng / pointCount,
    };
  };

  // Compute the default center as the centroid of Cluster 1's polygon
  const getDefaultCenter = () => {
    if (clusters && clusters.length > 0 && clusters[0].polygon && clusters[0].polygon.length > 0) {
      return calculatePolygonCentroid(clusters[0].polygon);
    }
    return { lat: 37.0902, lng: -95.7129 };
  };

  const markerIcon = {
    path: 'M10 20c-5.523 0-10-4.477-10-10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z',
    fillColor: '#FF0000',
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: '#FFFFFF',
    scale: 0.6,
  };

  // Auto-highlight the first cluster when the modal opens
  useEffect(() => {
    if (clusters && clusters.length > 0 && selectedClusters.length === 0) {
      setSelectedClusters([clusters[0]]);
      setSelectedClusterIndex(0);
      setProjectName(`Cluster 1 Project`);
      shouldPanMapRef.current = true; // Trigger map panning
    }
  }, [clusters]);

  // Pan and zoom the map when the selected clusters change and shouldPanMap is true
  useEffect(() => {
    if (!isLoaded || !mapRef.current || selectedClusters.length === 0 || !shouldPanMapRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    selectedClusters.forEach(cluster => {
      if (cluster.polygon && cluster.polygon.length > 0) {
        cluster.polygon.forEach(point => {
          bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
        });
      }
    });

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds);
    }

    // Reset the flag after panning
    shouldPanMapRef.current = false;
  }, [isLoaded, selectedClusters]);

  // Compute a convex hull for merged clusters
  const computeConvexHull = (points) => {
    const allPoints = points.flat();
    if (allPoints.length < 3) return allPoints;

    const hull = [];
    let basePoint = allPoints[0];
    for (const point of allPoints) {
      if (point.lat < basePoint.lat || (point.lat === basePoint.lat && point.lng < basePoint.lng)) {
        basePoint = point;
      }
    }

    hull.push(basePoint);
    let currentPoint = basePoint;
    while (true) {
      let nextPoint = allPoints[0] === currentPoint ? allPoints[1] : allPoints[0];
      for (const point of allPoints) {
        if (point === currentPoint) continue;
        const crossProduct =
          (point.lng - currentPoint.lng) * (nextPoint.lat - currentPoint.lat) -
          (point.lat - currentPoint.lat) * (nextPoint.lng - currentPoint.lng);
        if (crossProduct < 0) {
          nextPoint = point;
        }
      }
      if (nextPoint === hull[0]) break;
      hull.push(nextPoint);
      currentPoint = nextPoint;
    }

    return hull;
  };

  // Merge selected clusters into a single cluster
  const handleMergeClusters = () => {
    if (selectedClusters.length < 2) return;

    const mergedMeasurements = selectedClusters.flatMap(cluster => cluster.measurements);
    const allPoints = selectedClusters.flatMap(cluster => cluster.polygon);
    const newPolygon = computeConvexHull(allPoints);

    const mergedCluster = {
      id: `Merged Cluster ${selectedClusters.map(c => parseInt(c.id.split(' ')[1])).join(' & ')}`,
      polygon: newPolygon,
      measurements: mergedMeasurements,
      point_count: mergedMeasurements.length,
    };

    const newVisibleClusters = visibleClusters.filter(
      cluster => !selectedClusters.some(selected => selected.id === cluster.id)
    );
    setMergedClusters(prev => [...prev, mergedCluster]);
    setSelectedClusters([mergedCluster]);
    setSelectedClusterIndex(newVisibleClusters.length);
    setProjectName(`${mergedCluster.id} Project`);
    shouldPanMapRef.current = true; // Trigger map panning
  };

  const handleCreateProject = async () => {
    if (!projectName || selectedClusters.length === 0) return;
  
    setCreating(true);
    try {
      const primaryCluster = selectedClusters[0];
      const coords = primaryCluster.measurements[0].GpsCoordinates.split(',').map(Number);
      const masterWKT = `SRID=4326;POINT(${coords[1]} ${coords[0]})`;
  
      const { data: org, error: orgErr } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', primaryCluster.measurements[0].profile_id)
        .single();
  
      if (orgErr) throw orgErr;
  
      const { data: projectData, error: insertErr } = await supabase
        .from('projects')
        .insert([{
          name: projectName,
          master_gps: masterWKT,
          organization_id: org.organization_id,
          description: 'Auto-created from Smart Map cluster'
        }])
        .select();
  
      if (insertErr) throw insertErr;
  
      const newProject = projectData[0];
  
      const allMeasurements = selectedClusters.flatMap(cluster => cluster.measurements);
      const inserts = allMeasurements.map((m) => ({
        measurement_id: m.id,
        project_id: newProject.id,
        mapped_at: new Date(),
      }));
  
      const { error: mapErr } = await supabase
        .from('project_mapping')
        .insert(inserts);
  
      if (mapErr) throw mapErr;
  
      const primaryPolygon = selectedClusters.length === 1 ? primaryCluster.polygon : computeConvexHull(selectedClusters.flatMap(cluster => cluster.polygon));
      const polygonWKT = `SRID=4326;POLYGON((` +
        primaryPolygon.map(p => `${p.lng} ${p.lat}`).join(', ') +
        `, ${primaryPolygon[0].lng} ${primaryPolygon[0].lat}))`;
  
      const { error: geoError } = await supabase
        .from('projects')
        .update({ geofence_polygon: polygonWKT })
        .eq('id', newProject.id);
  
      if (geoError) throw geoError;
  
      selectedClusters.forEach(cluster => {
        setCreatedClusters(prev => new Set(prev).add(cluster.id));
      });
  
      // Show success message
      setSuccessMessage(`Project "${projectName}" created successfully!`);
  
      // Clear the success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
  
      // Jump to the next available cluster
      const newVisibleClusters = allClusters.filter(c => !createdClusters.has(c.id) && !selectedClusters.some(sc => sc.id === c.id));
      if (newVisibleClusters.length > 0) {
        const nextCluster = newVisibleClusters[0];
        const nextIndex = allClusters.findIndex(c => c.id === nextCluster.id);
        setSelectedClusters([nextCluster]);
        setSelectedClusterIndex(nextIndex);
        setProjectName(`Cluster ${nextIndex + 1} Project`);
        shouldPanMapRef.current = true; // Trigger map panning
      } else {
        // If no clusters remain, reset the selection but keep the modal open
        setSelectedClusters([]);
        setSelectedClusterIndex(0);
        setProjectName('');
      }
  
      if (onProjectCreated) onProjectCreated();
    } catch (err) {
      console.error('Smart Map Error:', err.message);
      alert('Failed to create project.');
    } finally {
      setCreating(false);
    }
  };

  const handlePreviousCluster = () => {
    if (selectedClusterIndex > 0) {
      const newIndex = selectedClusterIndex - 1;
      setSelectedClusterIndex(newIndex);
      setSelectedClusters([allClusters[newIndex]]);
      setProjectName(`Cluster ${newIndex + 1} Project`);
      shouldPanMapRef.current = true; // Trigger map panning
    }
  };

  const handleNextCluster = () => {
    if (selectedClusterIndex < allClusters.length - 1) {
      const newIndex = selectedClusterIndex + 1;
      setSelectedClusterIndex(newIndex);
      setSelectedClusters([allClusters[newIndex]]);
      setProjectName(`Cluster ${newIndex + 1} Project`);
      shouldPanMapRef.current = true; // Trigger map panning
    }
  };

  const handleClusterClick = (cluster, index, evt) => {
    const shift = evt.domEvent ? evt.domEvent.shiftKey : evt.shiftKey;
    const ctrl = evt.domEvent ? evt.domEvent.ctrlKey : evt.ctrlKey;
    const isMulti = shift || ctrl;

    if (isMulti) {
      setSelectedClusters(prev => {
        if (prev.some(c => c.id === cluster.id)) {
          return prev.filter(c => c.id !== cluster.id);
        }
        return [...prev, cluster];
      });
    } else {
      setSelectedClusters([cluster]);
      setSelectedClusterIndex(index);
    }

    const sel = isMulti
      ? `Merged Cluster ${
          (isMulti
            ? [...selectedClusters, cluster]
            : [cluster]
          )
          .map(c => parseInt(c.id.match(/\d+/)[0], 10))
          .join(' & ')
        } Project`
      : `Cluster ${index + 1} Project`;
    setProjectName(sel);
    shouldPanMapRef.current = true; // Trigger map panning
  };

  if (!isLoaded) {
    if (loadError) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="text-red-600">
              Failed to load Google Maps: {loadError.message}
            </div>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-300 text-gray-700 rounded">
              Close
            </button>
          </div>
        </div>
      );
    }
    return null;
  }

  const visibleClusters = clusters.filter((c) => !createdClusters.has(c.id));
  const allClusters = [...visibleClusters, ...mergedClusters];
  console.log('All Clusters:', allClusters);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {allClusters.length} Potential Projects Found
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        <div className="p-2 bg-orange-100 border border-orange-300 rounded mb-4 max-w-sm flex items-center">
          <ExclamationCircleIcon className="h-5 w-5 text-orange-500 inline-block mr-2" />
          <p className="text-sm text-orange-500 font-semibold">
            Shift + Click to select multiple projects to merge.
          </p>
        </div>

        <div
          className="w-full h-80 rounded-lg overflow-hidden mb-4 border border-gray-200"
          style={{ width: '100%', height: '320px' }}
        >
          <GoogleMap
            defaultCenter={getDefaultCenter()}
            zoom={13}
            mapContainerStyle={{ width: '100%', height: '100%' }}
            options={{ mapTypeId: 'hybrid' }}
            onLoad={(map) => {
                mapRef.current = map;
                console.log('Map Loaded:', map);
                window.google.maps.event.trigger(map, 'resize');
                map.panTo(getDefaultCenter());
                }}
            onUnmount={() => {
              mapRef.current = null;
            }}
          >
            {allClusters.map((cluster, index) => {
              const isSelected = selectedClusters.some(c => c.id === cluster.id);
              const centroid = calculatePolygonCentroid(cluster.polygon);
              const clusterNumber = cluster.id.includes('Merged')
                ? cluster.id.match(/\d+/g).join(' & ')
                : parseInt(cluster.id.split(' ')[1]);

              return (
                <React.Fragment key={cluster.id}>
                  <PolygonF
                    path={cluster.polygon}
                    options={{
                      fillColor: isSelected ? '#FFD700' : '#FF0000',
                      fillOpacity: 0.5,
                      strokeColor: isSelected ? '#FF9900' : '#CC0000',
                      strokeWeight: isSelected ? 3 : 1.5,
                      zIndex: isSelected ? 2 : 1,
                    }}
                    onClick={evt => handleClusterClick(cluster, index, evt)}
                  />
                 
                  {isSelected && (
                    <>
                      {cluster.measurements.slice(0, MAX_MARKERS).map((m) => {
                        const [lat, lng] = m.GpsCoordinates.split(',').map(Number);
                        return <MarkerF key={m.id} position={{ lat, lng }} icon={markerIcon} />;
                      })}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </GoogleMap>
        </div>

        {allClusters.length > 0 && (
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2">
              <button
                onClick={handlePreviousCluster}
                disabled={selectedClusterIndex === 0}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  selectedClusterIndex === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Previous
              </button>
              <button
                onClick={handleNextCluster}
                disabled={selectedClusterIndex === allClusters.length - 1}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  selectedClusterIndex === allClusters.length - 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Next
              </button>
              {selectedClusters.length > 1 && (
                <button
                  onClick={handleMergeClusters}
                  className="px-3 py-1 rounded-md text-sm font-medium bg-blue-500 text-white hover:bg-blue-600"
                >
                  Merge Selected Clusters
                </button>
              )}
            </div>
            <span className="text-sm text-gray-600">
              Cluster {selectedClusterIndex + 1} of {allClusters.length}
            </span>
          </div>
        )}

        
{successMessage && (
  <div className="p-2 bg-green-100 border border-green-300 rounded mb-4 max-w-sm flex items-center">
    <CheckCircleIcon className="h-5 w-5 text-green-500 inline-block mr-2" />
    <p className="text-sm text-green-500 font-semibold">{successMessage}</p>
  </div>
)}
        {selectedClusters.length > 0 && (
          <>
            <label className="block mb-2 text-sm font-medium text-gray-700">Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Cluster Project Name"
              className="w-full p-2 border border-gray-300 rounded mb-4"
            />

            <p className="text-sm text-gray-600 mb-4">
              {selectedClusters.length > 1 && (
                <span className="mr-2">{selectedClusters.length} clusters selected. </span>
              )}
              <strong>{selectedClusters.reduce((total, cluster) => total + cluster.measurements.length, 0)}</strong> measurements will be mapped.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setSelectedClusters([]);
                  onClose();
                }}
                className="px-4 py-2 rounded-md bg-gray-300 text-gray-700 hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                disabled={creating || !projectName}
                onClick={handleCreateProject}
                className="px-4 py-2 rounded-md bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-60"
              >
                {creating ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}